/*
    Created by Phat Pham
    phat@launchdeck.org
*/

import React, {
    useImperativeHandle,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    Text,
    View,
    FlatList,
    TouchableOpacity,
    StyleSheet,
    useWindowDimensions,
    Image,
    ActivityIndicator
} from 'react-native-web';
import PropTypes from 'prop-types';

// Custom Components
import { CustomModal, NavBar } from '../components';

// Utils
import { getCityList } from '../modules/api_cities';
import { showAppError } from '../utils/util_helpers';

// Configs
import GLOBAL from '../configs/config_global';
import LoadingView from './component_loading_view';

export function CityPickerModal(props, ref) {
    const { width } = useWindowDimensions();

    // region Props
    const {
        visibleCityIDs,
        selectedCityIDs,
        onCitySelected,
        singleSelect,
        useCheckBoxes,
        title,
        showSelectedCount,
        rightButtonText,
        useCollapsibleView,
        showDescription,
        liteMode
    } = props;
    // endregion

    // region Refs
    const mainList = useRef(null);
    // endregion

    // region Local State
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedIDs, setSelectedIDs] = useState(selectedCityIDs);
    const [loadingCities, setLoadingCities] = useState(false);
    const [compShouldLoad, setCompShouldLoad] = useState(false);
    // endregion

    // region Function - Show/Hide Modal
    const showModal = () => {
        setModalVisible(true);
        setSelectedIDs(selectedCityIDs);
        setTimeout(() => setCompShouldLoad(true), liteMode ? 0 : 1000);
    }

    const hideModal = () => {
        setModalVisible(false);
        setCompShouldLoad(false);
    }
    // endregion

    // region Expose Method
    useImperativeHandle(
        ref,
        () => ({ showModal, hideModal }),
        [selectedCityIDs]
    );
    // endregion

    // region Values
    const stateViewContainerWidth = useMemo(() => (width - (128 * 2) - 40), [width]);
    const imageWidth = (stateViewContainerWidth - (16 * 5)) / 5;
    const imageHeight = imageWidth * 9 / 16;
    const checkBoxMarginTop = 24;
    // endregion

    // region Function - On City Selected
    const _onCityPressed = (item) => {
        if (!singleSelect) {
            if (selectedIDs.includes(item.id)) {
                setSelectedIDs(selectedIDs.filter((id) => id !== item.id));
            }
            else setSelectedIDs(selectedIDs.concat(item.id));
        }
        else {
            setSelectedIDs([item.id]);
            onCitySelected(item.id, item.name);
            hideModal();
        }
    };
    // endregion

    // region Render City Item
    const renderCityItem = (item, index) => (
        <TouchableOpacity
            key={`cityItem_${item.id}`}
            onPress={() => _onCityPressed(item)}
            style={{ width: imageWidth, height: liteMode ? 64 : imageHeight, marginRight: ((index % 5 === 0) && (index % 2 !==0)) ? 0 : 16, marginTop: 16 }}>
            {/* Image */}
            {
                !liteMode &&
                <Image
                    source={{ uri: item['image_webp'] }}
                    style={{ width: imageWidth, height: imageHeight, borderRadius: 8 }}
                    resizeMode={'cover'}
                />
            }

            {/* Overlay */}
            <View style={[
                StyleSheet.absoluteFill,
                {
                    backgroundColor:
                        (selectedIDs.includes(item.id)) ?
                            'rgba(50, 215, 112, 0.7)'
                            :
                            'rgba(0, 0, 0, 0.3)',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 8
                }
            ]}>
                {/* Name */}
                <Text style={{ color: 'white', fontWeight: 'bold', fontSize: 18, marginHorizontal: 16, textAlign: 'center' }}>
                    {item.name}
                </Text>
            </View>
        </TouchableOpacity>
    );
    // endregion

    // region Render City Item - Check Box
    const renderCityItemCheckBox = (item, index) => {
        const isChosen = selectedIDs.includes(item.id);
        return(
            <TouchableOpacity
                key={`cityItem_${item.id}`}
                onPress={() => _onCityPressed(item)}
                style={{ flexDirection: 'row', alignItems: 'center', alignSelf: 'stretch', marginRight: ((index % 5 === 0) && (index % 2 !==0)) ? 0 : 32, marginTop: 24 }}>
                {/* Check Box */}
                <View style={{
                    borderWidth: 2, borderRadius: 4, borderColor: isChosen ? GLOBAL.COLOR.MAIN : GLOBAL.COLOR.TEXT_GRAY,
                    backgroundColor: isChosen ? GLOBAL.COLOR.MAIN : 'white', height: 24, width: 24,
                    alignItems: 'center', justifyContent: 'center'
                }}>
                    <Image
                        source={require('../images/check.png')}
                        style={{ width: 16, height: 16, tintColor: 'white' }}
                    />
                </View>

                {/* Name */}
                <Text style={{ color: GLOBAL.COLOR.BASIC.BLACK_TWO, fontWeight: '500', fontSize: 16, marginLeft: 16 }} numberOfLines={1}>
                    {item.name}
                </Text>
            </TouchableOpacity>
        );
    };
    // endregion

    // region Render Non-Collapsible State Item
    const renderStateItemNonCollapsible = (state) => {
        const filteredCityList = state.cityList.filter((city) => visibleCityIDs.length > 0 ? visibleCityIDs.includes(city.id) : true);
        return(
            <View style={{ alignSelf: 'stretch', marginTop: 36 }}>
                {/* State Name */}
                <Text style={{ fontSize: 22, fontWeight: 'bold' }}>
                    {`${state.name} (${filteredCityList.length})`}
                </Text>

                {/* City List */}
                <View style={{ alignSelf: 'stretch', flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}>
                    {filteredCityList.map((city, index) => useCheckBoxes ? renderCityItemCheckBox(city, index) : renderCityItem(city, index))}
                </View>
            </View>
        );
    };
    // endregion

    // region MAIN RENDER
    return(
        <CustomModal visible={modalVisible}>
            {/* Nav Bar */}
            <NavBar
                leftButtonOnPress={() => hideModal()}
                title={title}
                subTitle={showSelectedCount ? `${selectedIDs.length} cit${selectedIDs.length > 1 ? 'ies' : 'y'} selected` : null}
                rightButtonTitle={singleSelect ? null : rightButtonText}
                rightButtonOnPress={() => {
                    if (!singleSelect) {
                        onCitySelected(selectedIDs);
                        hideModal();
                    }
                }}
            />

            {/* Description */}
            {
                showDescription &&
                <Text style={{ fontSize: 13, color: GLOBAL.COLOR.TEXT_GRAY, marginTop: 24, marginBottom: useCollapsibleView ? 16 : 0, alignSelf: 'center' }}>
                    Only showing cities that your truck operates in
                </Text>
            }

            {/* Main Content */}
            {
                GLOBAL.GROUPED_STATES.length > 0 ?
                    (
                        compShouldLoad ?
                            <FlatList
                                ref={mainList}
                                style={{ flex: 1, width: '100%' }}
                                contentContainerStyle={{ paddingBottom: 48, paddingLeft: 24 }}
                                data={
                                    visibleCityIDs.length === 0 ?
                                        GLOBAL.GROUPED_STATES
                                        :
                                        GLOBAL.GROUPED_STATES.filter((state) => state.cityList.find((city) => visibleCityIDs.includes(city.id)))
                                }
                                renderItem={({ item }) => renderStateItemNonCollapsible(item)}
                                showsVerticalScrollIndicator={false}
                                keyExtractor={(item) => item.id.toString()}
                            />
                            :
                            <LoadingView isLoading={true} />
                    )
                    :
                    <View style={{ flex: 1, width: '100%', alignItems: 'center', justifyContent: 'center', padding: 36 }}>
                        {
                            loadingCities ?
                                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                    <ActivityIndicator color={GLOBAL.COLOR.TEXT_GRAY} />

                                    <Text style={{ color: GLOBAL.COLOR.TEXT_GRAY, marginLeft: 8 }}>
                                        Loading city data...
                                    </Text>
                                </View>
                                :
                                <TouchableOpacity
                                    onPress={() => {
                                        setLoadingCities(true);
                                        getCityList()
                                            .catch((e) => showAppError(e))
                                            .finally(() => setLoadingCities(false));
                                    }}>
                                    <Text style={{ textAlign: 'center', color: GLOBAL.COLOR.TEXT_GRAY, lineHeight: 24 }}>
                                        Unable to load city data,{'\n'}tap this message to try again
                                    </Text>
                                </TouchableOpacity>
                        }
                    </View>
            }
        </CustomModal>
    );
    // endregion
}

// Forward Ref
CityPickerModal = React.forwardRef(CityPickerModal);

// region Props
CityPickerModal.propTypes = {
    visibleCityIDs: PropTypes.array,
    onCitySelected: PropTypes.func,
    selectedCityIDs: PropTypes.array,
    singleSelect: PropTypes.bool,
    useCheckBoxes: PropTypes.bool,
    title: PropTypes.string,
    showSelectedCount: PropTypes.bool,
    rightButtonText: PropTypes.string,
    useCollapsibleView: PropTypes.bool,
    showDescription: PropTypes.bool
};

CityPickerModal.defaultProps = {
    visibleCityIDs: [],
    onCitySelected: () => {},
    selectedCityIDs: [],
    singleSelect: false,
    useCheckBoxes: false,
    title: 'Select Serving Cities',
    showSelectedCount: false,
    rightButtonText: 'Done',
    useCollapsibleView: true,
    showDescription: false
};
// endregion
